"use client";

import { Suspense, useState } from "react";
import { useSearchParams } from "next/navigation";
import { T } from "@/lib/i18n/T";
import { DASH_MENU, DASH_USER } from "./dashboardData";
import { DashboardRail } from "./DashboardNav";
import { ImagesStoreBox } from "./ImagesStoreBox";
import { MyInfo } from "./MyInfo";
import { HelpCenter } from "./HelpCenter";

/** Empty-state copy per section (the old dashboard shipped these stubs). */
const SECTION_EMPTY: Record<string, string> = {
  wishlist: "dashboard.empty.wishlist",
  recent: "dashboard.empty.recent",
  orders: "dashboard.empty.orders",
  info: "dashboard.empty.info",
  notifications: "dashboard.empty.notifications",
  affiliates: "dashboard.empty.affiliates",
  gifts: "dashboard.empty.gifts",
};

// Sections with a dedicated component (not the generic empty-state copy above).
const RICH_SECTIONS = new Set(["info", "help"]);

const WISHLIST = [
  "https://cdn.shopify.com/s/files/1/0293/9277/files/11-12-25_S2_14_S774711FN_Wine_ZSR_KP_EO_11-18-29_10904_PXF.jpg",
  "https://cdn.shopify.com/s/files/1/0293/9277/files/12-04-25_S8_45_ZD8219_Black_TK_PC_15-02-11_18723_PXF.jpg",
  "https://cdn.shopify.com/s/files/1/0293/9277/files/12-09-25_S1_45_ZDMKLLP71822_Fuchsia_AB_NL_14-58-04_100649_PXF.jpg",
  "https://cdn.shopify.com/s/files/1/0293/9277/files/12-05-25_S7_29_ZDMKLLP71822_BlackTK_RL_14-59-55_2956_PXF.jpg",
];

// Placeholder imagery (reordered) until the real "recently viewed" feed lands.
const RECENT = [WISHLIST[2], WISHLIST[3], WISHLIST[0], WISHLIST[1]];

/** Headings for sections reachable from the home teasers but not in the rail. */
const EXTRA_TITLES: Record<string, string> = {
  wishlist: "dashboard.wishlist",
  recent: "dashboard.recent",
};

const isValidSection = (s: string | null): s is string =>
  s === "dashboard" || (!!s && (s in SECTION_EMPTY || RICH_SECTIONS.has(s)));

/**
 * Account dashboard. Sections switch via in-memory state (no route change), so
 * the menu is instant. The active section is mirrored to `?s=` with
 * `replaceState` (no re-render / no navigation), and restored from `?s=` on
 * load so refreshes and deep links (e.g. a notification opening "info") land
 * on the right section.
 */
function DashboardView() {
  const params = useSearchParams();
  const fromUrl = params.get("s");
  const [section, setSection] = useState(() => (isValidSection(fromUrl) ? fromUrl : "dashboard"));

  const select = (key: string) => {
    setSection(key);
    const { pathname } = window.location;
    const url = key === "dashboard" ? pathname : `${pathname}?s=${key}`;
    window.history.replaceState(null, "", url);
  };

  // Wishlist / Recently viewed aren't in the rail menu — their headings live here.
  const titleId =
    DASH_MENU.find((m) => m.key === section)?.titleId ?? EXTRA_TITLES[section] ?? "";

  return (
    <div className="rubyn__dashboard-container csx3-R00 csx3-g-24">
      <DashboardRail active={section} onSelect={select} />

      <div className="rubyn__dashboard-container-r csx3-f1 csx3-C00 csx3-g-24">
        <h2 className="rubyn__dashboard-title">
          {section === "dashboard" ? (
            <T id="dashboard.greeting" values={{ name: DASH_USER.name }} />
          ) : titleId ? (
            <T id={titleId} />
          ) : null}
        </h2>

        {section === "dashboard" ? (
          <div className="rubyn__dashboard-boxes csx3-R00 csx3-fw csx3-g-24 csx3-w-full">
            <ImagesStoreBox titleId="dashboard.wishlist" images={WISHLIST} onOpen={() => select("wishlist")} />
            <ImagesStoreBox titleId="dashboard.recent" images={RECENT} onOpen={() => select("recent")} />
          </div>
        ) : section === "info" ? (
          <MyInfo />
        ) : section === "help" ? (
          <HelpCenter />
        ) : (
          <p className="rubyn__dashboard-empty"><T id={SECTION_EMPTY[section]} /></p>
        )}
      </div>
    </div>
  );
}

/** `useSearchParams` needs a Suspense boundary for the static (Capacitor) export. */
export default function DashboardPage() {
  return (
    <Suspense>
      <DashboardView />
    </Suspense>
  );
}
